"use client";

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import {
  type BillingInterval,
  type PlanId,
  type StripeSubscriptionStateLike,
} from "@reelmagic/domain";
import { useFirebaseAuth } from "@/components/auth/AuthProvider";
import { getBillingSetupChecklist, getStripeExtensionConfig } from "@/lib/billing/stripe-extension";
import { getFirebaseClientServices } from "@/lib/firebase/client";
import { getBillingPlanSummary } from "@/lib/billing/plan-summary";
import { reportCaughtError } from "@/lib/sentry/report-caught-error";

type BillingPlanState =
  | { status: "ready"; planId: PlanId; hasPaidPlan: boolean; hasActiveSubscription: boolean; billingInterval: BillingInterval | null }
  | { status: "loading" | "unavailable"; planId: null; hasPaidPlan: false; hasActiveSubscription: false; billingInterval: null };

type AccountPlan = { uid: string; state: BillingPlanState };
const BillingPlanContext = createContext<BillingPlanState | null>(null);

export function BillingPlanProvider({ children }: { children: ReactNode }) {
  const { user } = useFirebaseAuth();
  const uid = user?.uid;
  const { stripeExtensionConfigured } = getBillingSetupChecklist();
  const [accountPlan, setAccountPlan] = useState<AccountPlan | null>(null);

  useEffect(() => {
    if (!uid || !stripeExtensionConfigured) return;
    const { firestore } = getFirebaseClientServices();
    const { customersCollection } = getStripeExtensionConfig();
    let active = true;
    const unsubscribe = onSnapshot(
      collection(firestore, customersCollection, uid, "subscriptions"),
      (snapshot) => {
        if (!active) return;
        const subscriptions = snapshot.docs.map((document) => document.data() as StripeSubscriptionStateLike);
        setAccountPlan({ uid, state: { status: "ready", ...getBillingPlanSummary(subscriptions) } });
      },
      (error) => {
        if (!active) return;
        reportCaughtError(error, { operation: "billing.stream_plan" });
        setAccountPlan({ uid, state: { status: "unavailable", planId: null, hasPaidPlan: false, hasActiveSubscription: false, billingInterval: null } });
      },
    );
    return () => { active = false; unsubscribe(); };
  }, [uid, stripeExtensionConfigured]);

  const state = useMemo<BillingPlanState>(() => {
    if (!stripeExtensionConfigured) return { status: "unavailable", planId: null, hasPaidPlan: false, hasActiveSubscription: false, billingInterval: null };
    if (!uid || accountPlan?.uid !== uid) return { status: "loading", planId: null, hasPaidPlan: false, hasActiveSubscription: false, billingInterval: null };
    return accountPlan.state;
  }, [accountPlan, uid, stripeExtensionConfigured]);

  return <BillingPlanContext.Provider value={state}>{children}</BillingPlanContext.Provider>;
}

export function useBillingPlan() {
  const state = useContext(BillingPlanContext);
  if (!state) throw new Error("useBillingPlan must be used inside BillingPlanProvider.");
  return state;
}
